import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SUPPORTED_LANGUAGES, languageSelectorLabel, type LanguageCode } from '@/config/languages';
import { uiLocales } from '@/i18n/negotiate';
import { useLocale } from '@/i18n/useLocale';
import { useChooseLanguage } from '@/components/layout/useChooseLanguage';
import { PendingDecision } from '@/components/editorial/PendingDecision';

/**
 * The Settings "Language" section (#2109 R1 lane L4): a labelled select over
 * the languages that have a UI catalog, showing `native name (English name)`.
 * Picking one applies at once and persists to `User.preferences.language`.
 *
 * It offers the UI locales (`uiLocales()`), not the whole declaration:
 * Filipino is a valid stored preference for chat translation but has no UI
 * catalog and would render English under a Filipino label, which is the kind
 * of quiet misrepresentation this prototype does not do.
 *
 * Every language is offered behind the machine-translation banner until TRF
 * names a reviewer for it (DECISIONS 2026-09-23).
 *
 * Staff pages are English-only (DECISIONS 2026-09-30): while the active role
 * is TRFer or admin the select is replaced by a one-line note, and the saved
 * language is left untouched for the person's parent pages.
 */
export function LanguageSelect() {
  const { t } = useTranslation('settings');
  const { locale, staffEnglishOnly } = useLocale();
  const choose = useChooseLanguage();
  const offered = uiLocales();
  const options = SUPPORTED_LANGUAGES.filter((l) => offered.includes(l.code));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{t('language.heading')}</CardTitle>
        <CardDescription>{t('language.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {staffEnglishOnly ? (
          <p className="text-body-sm text-stevens-ink-2">{t('language.staffEnglishOnly')}</p>
        ) : (
          <>
            {/* #2109 R3-B16 and R3-B26: the per-string machine mark and the
                Spanish-first release are decisions TRF may reverse. */}
            <div className="flex items-center gap-1">
              <Label htmlFor="ui-language">{t('language.label')}</Label>
              <PendingDecision id="machine-mark-glyph" side="right" />
              <PendingDecision id="es-first-release" side="right" />
            </div>
            <select
              id="ui-language"
              value={locale}
              onChange={(e) => void choose(e.target.value as LanguageCode)}
              className="flex h-10 w-full max-w-xs items-center rounded-md border border-input bg-background px-3 py-0 text-body ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {options.map((lang) => (
                <option key={lang.code} value={lang.code} lang={lang.bcp47}>
                  {languageSelectorLabel(lang)}
                </option>
              ))}
            </select>
            <p className="text-body-sm text-stevens-ink-3">{t('language.helper')}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
